import { ledgerDateKey, ledgerMoney } from 'components/TrustLedgerGrid'
import { type InputType, loanOptionsConstants } from 'config'
import type { FormTableHeader } from 'stories/components'
import { removeComma } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

/**
 * Construction Reserve columns.
 *
 * Most cells here are composites built in `ConstructionReserveTab` - two stacked figures under
 * one heading - so each sortable column names the underlying field it orders by through
 * `sortValue`, and amounts sort by value rather than by the digits of a formatted string.
 */
export const header: FormTableHeader[] = [
  { key: 'ID', title: 'Order ID', sortable: true, sortValue: (row) => Number(row.id) || 0 },
  { key: 'type', title: 'Type', sortable: true },
  {
    key: 'referenceWithFrom',
    title: `<span><div class="border-b w-fit mb-1 border-gray-300">Reference</div>From Whom Received/Paid</span>`,
    sortable: true,
    sortValue: (row) => row.reference || '',
  },
  {
    key: 'eligibleWithNetAmount',
    title: `<span><div class="border-b w-fit mb-1 border-gray-300">Eligible Amount</div>Net Eligible Amount</span>`,
    sortable: true,
    sortValue: (row) => removeComma(row.eligibleAmount),
  },
  {
    key: 'recommendedDisbursement',
    title: 'Max Eligible Disbursement',
    sortable: true,
    sortValue: (row) => removeComma(row.recommendedDisbursement),
    format: (row) => ledgerMoney(row.recommendedDisbursement),
  },
  {
    key: 'previouslyDisbursed',
    title: 'Previously Disbursed',
    sortable: true,
    sortValue: (row) => removeComma(row.previouslyDisbursed),
    format: (row) => ledgerMoney(row.previouslyDisbursed),
  },
  {
    key: 'paymentWithDeposit',
    title: `<span><div class="border-b w-fit mb-1 border-gray-300">Payment</div>Deposit</span>`,
    sortable: true,
    sortValue: (row) => removeComma(row.payment),
  },
  {
    key: 'feeWithAddHoldback',
    title: `<span><div class="border-b w-fit mb-1 border-gray-300">Fee</div>Additional Holdback</span>`,
    sortable: true,
    sortValue: (row) => removeComma(row.fee),
  },
  {
    key: 'drawWithBalance',
    title: `<span><div class="border-b w-fit mb-1 border-gray-300">Draw Disbursed</div>Balance</span>`,
    sortable: true,
    sortValue: (row) => removeComma(row.balance),
  },
  {
    key: 'dateWithFiles',
    title: `<span><div class="border-b w-[90px] mb-1 border-gray-300">Date</div>Files</span>`,
    sortable: true,
    sortValue: (row) => ledgerDateKey(row.date),
  },
]

export const additionalInputs = (): Record<string, InputType> => {
  return {
    isWiringInstructions: {
      inputType: 'toggle',
      value: false,
      title: 'Wiring Instructions',
      span: 2,
    },
    pledgedFacility: {
      inputType: 'text',
      type: 'text',
      title: 'Pledged Facility',
      span: 1,
      history: true,
    },
    interestType: {
      inputType: 'select',
      options: loanOptionsConstants.interestType,
      hasDefaultOption: true,
      title: 'Interest Type',
      history: true,
    },
    accountName: {
      inputType: 'text',
      type: 'text',
      title: 'Name on Account',
      history: true,
      visible: false,
    },
    accountAddress: {
      inputType: 'map',
      title: 'Address on Account',
      history: true,
      visible: false,
    },
    routingNumber: {
      inputType: 'text',
      type: 'routingNo',
      title: 'Routing Number',
      history: true,
      maxLength: 9,
      visible: false,
    },
    accountNumber: {
      inputType: 'text',
      type: 'bankAccountNo',
      title: 'Account Number',
      history: true,
      minLength: 8,
      maxLength: 17,
      visible: false,
    },
    bankName: {
      inputType: 'text',
      type: 'text',
      title: 'Bank Name',
      history: true,
      visible: false,
    },
    comment: {
      inputType: 'textarea',
      title: 'Comment',
      visible: false,
      history: true,
    },
    interestComment: {
      inputType: 'textarea',
      title: 'Interest Reserve Comment',
      visible: false,
      history: true,
    },
  }
}

export const defaultInputs = (): Record<string, InputType> => {
  const typeVal = (inputs: Record<string, InputType>) => inputs.type?.value
  const isDraw = (inputs: Record<string, InputType>) => typeVal(inputs) === 'Draw'
  const isTransfer = (inputs: Record<string, InputType>) => typeVal(inputs) === 'Transfer'
  const isDeposit = (inputs: Record<string, InputType>) => typeVal(inputs) === 'Deposit'
  const isDisbursement = (inputs: Record<string, InputType>) => typeVal(inputs) === 'Disbursement'
  const isAdjustment = (inputs: Record<string, InputType>) => typeVal(inputs) === 'Adjustment'
  // A category sub-type is required on Deposit / Disbursement.
  const hasSubType = (inputs: Record<string, InputType>) => isDeposit(inputs) || isDisbursement(inputs)
  // Credit (money in): Deposit, Adjustment. Debit (money out): Draw, Disbursement, Adjustment.
  const showsCredit = (inputs: Record<string, InputType>) => isDeposit(inputs) || isAdjustment(inputs)
  const showsDebit = (inputs: Record<string, InputType>) =>
    isDraw(inputs) || isDisbursement(inputs) || isAdjustment(inputs)
  // Payer / payee / source of funds (not Transfer, which has its own).
  const showsFromWhom = (inputs: Record<string, InputType>) =>
    isDeposit(inputs) || isDisbursement(inputs) || isDraw(inputs)
  const inputs: Record<string, InputType> = {
    type: {
      inputType: 'select',
      options: ['Deposit', 'Disbursement', 'Draw', 'Transfer', 'Adjustment'],
      title: 'Type',
      value: 'Draw',
      required: true,
    },
    bankAccountId: {
      inputType: 'select',
      title: 'Bank Account',
      hasDefaultOption: true,
      defaultOptionText: '',
      options: {},
      sort: true,
      value: '',
      error: '',
    },
    subType: {
      inputType: 'select',
      options: {
        openingBalance: 'Opening Balance',
        borrowerPayment: 'Borrower Payment',
        escrowFunding: 'Escrow Funding',
        holdbackFunding: 'Holdback Funding',
        payoffProceeds: 'Payoff Proceeds',
        wireReceipt: 'Wire Receipt',
        tax: 'Tax',
        insurance: 'Insurance',
        investorRemittance: 'Investor Remittance',
        payoff: 'Payoff',
        other: 'Other',
      },
      hasDefaultOption: true,
      title: 'Category',
      value: '',
      required: true,
      visible: hasSubType,
    },
    date: {
      inputType: 'text',
      type: 'date',
      title: 'Date',
      value: '',
      error: '',
      required: true,
    },
    reference: {
      inputType: 'text',
      type: 'text',
      title: 'Reference',
      value: '',
      error: '',
      required: true,
    },
    amount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Amount',
      prefix: '$',
      value: '',
      error: '',
      required: true,
      visible: isTransfer,
    },
    destinationAccount: {
      inputType: 'select',
      options: {
        interest: 'Interest Reserve Ledger',
        escrow: 'Impounds',
        suspense: 'Suspense',
        escrowRepairHoldback: 'Escrow Repair Holdback',
      },
      hasDefaultOption: true,
      title: 'Destination Account',
      value: '',
      error: '',
      required: true,
      visible: isTransfer,
    },
    fromWhom: {
      inputType: 'text',
      type: 'text',
      title: 'From Whom Received/Paid',
      value: '',
      error: '',
      required: true,
    },
    /**
     * Which draw inspection this draw was taken against.
     *
     * The options are the loan's own inspection orders, filled in by the screen (see
     * `components/DrawAdvisory`); the value is that component's `source:id` key, which the screen
     * splits into the `inspectionOrderId` / `inspectionOrderSource` pair the row stores. Empty is
     * a legitimate answer: an inspection has never been a condition of a draw here and this does
     * not make it one.
     */
    inspectionOrder: {
      inputType: 'select',
      title: 'Inspection',
      hasDefaultOption: true,
      defaultOptionText: 'None on file',
      options: {},
      value: '',
      error: '',
      span: 2,
    },
    /**
     * Who funded this advance. Rendered by the screen through `render`, because it is a table of
     * participants rather than a field; declared here so it travels with the rest of the draw and
     * is saved by the same submit.
     */
    allocations: {
      inputType: 'custom',
      title: 'Funded by',
      value: [],
      span: 3,
      render: () => null,
    },
    eligibleAmount: {
      inputType: 'text',
      type: 'thousandSep',
      // Cumulative, and the field has to say so: every draw type nets Previously Disbursed off
      // this figure, so a servicer entering just the current draw gets a negative recommendation.
      title: 'Gross Eligible Amount (Cost to Date)',
      prefix: '$',
      value: '',
      error: '',
    },
    netEligibleAmount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Net Eligible Amount of Gross',
      prefix: '$',
      value: '',
      error: '',
      disabled: true,
    },
    previouslyDisbursed: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Previously Disbursed',
      prefix: '$',
      value: '',
      error: '',
      readOnly: true,
    },
    grossEligibleDrawAmount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'New Eligible Amount',
      prefix: '$',
      value: '',
      error: '',
      readOnly: true,
    },
    inspectionFee: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Inspection Fee',
      prefix: '$',
      value: '',
      error: '',
      required: true,
    },
    wireFee: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Wire Fee',
      prefix: '$',
      value: '',
      error: '',
      required: true,
    },
    titleUpdateFee: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Title Update Fee',
      prefix: '$',
      value: '',
      error: '',
      required: true,
    },
    additionHoldback: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Additional Holdback',
      prefix: '$',
      value: '',
      error: '',
      required: true,
    },
    recommendedDisbursement: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Max Eligible Disbursement',
      prefix: '$',
      value: '',
      error: '',
      readOnly: true,
    },
    payment: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Disbursement',
      prefix: '$',
      value: '',
      error: '',
      required: true,
    },
    deposit: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Deposit',
      prefix: '$',
      value: '',
      error: '',
      required: true,
    },
    totalDraw: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Total Draw',
      prefix: '$',
      value: '',
      error: '',
      readOnly: true,
    },
    notes: {
      inputType: 'textarea',
      title: 'Note',
      value: '',
      error: '',
      span: 2,
    },
    files: {
      inputType: 'FileTable',
      title: 'Files',
      value: [],
      error: '',
      span: 3,
      filePath: '',
      required: false,
    },
  }

  // Draw-only detail fields (eligibility, fees, holdback, computed totals).
  const drawOnlyKeys = [
    'inspectionOrder',
    'allocations',
    'eligibleAmount',
    'netEligibleAmount',
    'previouslyDisbursed',
    'grossEligibleDrawAmount',
    'inspectionFee',
    'wireFee',
    'titleUpdateFee',
    'additionHoldback',
    'recommendedDisbursement',
    'totalDraw',
  ]
  drawOnlyKeys.forEach((key) => {
    inputs[key].visible = isDraw
  })
  // Money movement: strict signing — Disbursement (debit) vs Deposit (credit).
  inputs.payment.visible = showsDebit
  inputs.deposit.visible = showsCredit
  inputs.fromWhom.visible = showsFromWhom
  // notes and files apply to every type (left always-visible).

  // Default blank numeric fields to 0 so the add modal shows 0 instead of empty.
  Object.values(inputs).forEach((input: any) => {
    if (
      ['thousandSep', 'thousandSepNoDecimal', 'number'].includes(input.type) &&
      (input.value === '' || input.value == null)
    ) {
      input.value = '0'
    }
  })

  return inputs
}

export const afxDefaultInputs = (): Record<string, InputType> => {
  const loanNumber = setLoanNumber()
  return {
    message: {
      inputType: 'textarea',
      title: 'Message',
      value: '',
      error: '',
    },
    files: {
      inputType: 'FileTable',
      title: 'Files',
      value: [],
      error: '',
      filePath: `loan/${loanNumber}/trustLedger/afx`,
      required: false,
      showCategory: false,
      showStatus: false,
    },
  }
}

// --- Sub-ledger tabs (Draw disbursement / Escrow Repair Holdback / Impounds / Interest Reserve / Suspense) ---

export const interestHeader: FormTableHeader[] = [
  { key: 'reference', title: 'Reference', sortable: true },
  { key: 'fromWhom', title: 'From Whom Received/Paid', sortable: true },
  {
    key: 'payment',
    title: 'Payment',
    sortable: true,
    sortValue: (row) => removeComma(row.payment),
    format: (row) => ledgerMoney(row.payment),
  },
  {
    key: 'deposit',
    title: 'Deposit',
    sortable: true,
    sortValue: (row) => removeComma(row.deposit),
    format: (row) => ledgerMoney(row.deposit),
  },
  {
    key: 'balance',
    title: 'Balance',
    sortable: true,
    sortValue: (row) => removeComma(row.balance),
    format: (row) => ledgerMoney(row.balance),
  },
  { key: 'date', title: 'Date', sortable: true, sortValue: (row) => ledgerDateKey(row.date) },
]

export const interestDefaultInputs = (): Record<string, InputType> => {
  return {
    date: {
      inputType: 'text',
      type: 'date',
      title: 'Date',
      value: '',
      error: '',
      required: true,
    },
    reference: {
      inputType: 'text',
      type: 'text',
      title: 'Reference',
      value: '',
      error: '',
      required: true,
    },
    fromWhom: {
      inputType: 'text',
      type: 'text',
      title: 'From Whom Received/Paid',
      value: '',
      error: '',
      required: true,
    },
    payment: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Payment',
      prefix: '$',
      value: '',
      error: '',
      required: true,
    },
    deposit: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Deposit',
      prefix: '$',
      value: '',
      error: '',
      required: true,
    },
  }
}

/**
 * Tabs on the Trust Ledger screen, in render order. `all` leads and is the default landing
 * tab: it is the read-only roll-up across every category. `constructionReserve` is the
 * trust-ledger table itself, followed by the sub-ledgers that moved here from the old
 * standalone Escrow Ledger screen.
 *
 * There is no Draw disbursement tab: its rows were mirrored from the construction ledger, so it
 * showed the same draws as `constructionReserve` in a thinner shape. The two are one tab now, and
 * that tab carries the Remaining Construction Reserve figure the draw tab used to own.
 */
export const TRUST_LEDGER_TABS: Record<string, string> = {
  all: 'All',
  constructionReserve: 'Construction Reserve',
  origination: 'Origination Trust',
  escrowRepairHoldback: 'Escrow Repair Holdback Ledger',
  escrow: 'Impounds Ledger',
  interest: 'Interest Reserve Ledger',
  suspense: 'Suspense',
}

/** Tab the screen opens on when the URL does not name one. */
export const TRUST_LEDGER_DEFAULT_TAB = 'all'

/** Sub-ledger tabs, i.e. every tab backed by TrustLedgerInterest rows. */
export const TRUST_LEDGER_SUB_TABS = [
  'origination',
  'escrowRepairHoldback',
  'escrow',
  'interest',
  'suspense',
]

/** Category chip colors on the All tab. */
export const TRUST_LEDGER_CATEGORY_COLORS: Record<string, string> = {
  constructionReserve: 'bg-shade-blue/10 text-shade-blue',
  origination: 'bg-indigo-100 text-indigo-800',
  draw: 'bg-amber-100 text-amber-800',
  escrowRepairHoldback: 'bg-purple-100 text-purple-800',
  escrow: 'bg-emerald-100 text-emerald-800',
  interest: 'bg-sky-100 text-sky-800',
  suspense: 'bg-rose-100 text-rose-800',
}
